/**
 * Inference Plugin Adapter
 *
 * Converts the InferencePlugin shape (domain-specific, ergonomic)
 * into a PluginDeclaration (unified registry format).
 *
 * Generates:
 *   - ToolDefinition (AI tool with schema + execute handler)
 *   - PluginContextMenu (right-click on image/video objects)
 *   - PluginSlashCommand (if provided)
 */

import type { SceneObject, TraceScene } from 'trace-viewer/scene';
import type { ToolDefinition } from '../ai/tools/types';
import type { ToolContext } from '../ai/agent-helpers';
import { applyTransform, resolveObject } from '../ai/agent-helpers';
import { isImageSceneObject, isVideoSceneObject, isGeoTiffSceneObject, type InferenceStatusCallback } from '../inference/shared';
import type { PluginDeclaration, PluginSlashCommand, PluginContextMenu } from './types';
import { registerPlugin } from './registry';

// ── Inference Plugin Shape ──────────────────────────────────

export interface InferencePluginOptions {
    /** Unique id, e.g. 'sharp', 'pointcloud' */
    id: string;
    /** 'image', 'video', or 'geotiff' — determines applicable SceneObjects */
    sourceType: 'image' | 'video' | 'geotiff';

    // Context menu
    menuLabel: string;
    menuOrder: number;

    // AI tool
    toolName: string;
    extraInputProperties?: Record<string, unknown>;
    objectOptional?: boolean;
    toolModelLabel?: string;

    // Execution
    run: (sourceObject: SceneObject, traceScene: TraceScene, onStatus?: InferenceStatusCallback) => Promise<SceneObject>;
    executeToolOverride?: (input: Record<string, unknown>, ctx: ToolContext) => Promise<string>;

    // Slash command (optional — declare inline with the plugin)
    slashCommand?: PluginSlashCommand;
}

// ── Standard Input Properties ───────────────────────────────

const VEC3 = { type: 'array', items: { type: 'number' } };
const COMMON_INPUT_PROPERTIES = { object: { type: 'string' }, name: { type: 'string' }, position: VEC3, rotation: VEC3, scale: VEC3 };

// ── Tool Builder ────────────────────────────────────────────

const SOURCE_TYPE_CHECKS = {
    image: isImageSceneObject,
    video: isVideoSceneObject,
    geotiff: isGeoTiffSceneObject,
} as const;

function buildDefaultToolExecute(
    opts: InferencePluginOptions,
): (input: Record<string, unknown>, ctx: ToolContext) => Promise<string> {
    const typeCheck = SOURCE_TYPE_CHECKS[opts.sourceType];
    const typeLabel = opts.sourceType;
    // Image-typed tools also accept Video objects — the current frame is
    // captured transparently by getImageBlob.
    const acceptsVideoAsImage = opts.sourceType === 'image';

    return async (input, ctx) => {
        const objectRef = input.object as string;
        const name = input.name as string | undefined;
        const position = input.position as [number, number, number] | undefined;
        const rotation = input.rotation as [number, number, number] | undefined;
        const scale = input.scale as [number, number, number] | undefined;
        const { traceScene } = ctx;

        const target = resolveObject(objectRef, ctx);
        if (!target) return `Error: Object "${objectRef}" not found.`;
        const typeOk = typeCheck(target) || (acceptsVideoAsImage && isVideoSceneObject(target));
        if (!typeOk) {
            return `Error: "${objectRef}" is not a ${typeLabel} object.`;
        }

        try {
            // The context-menu path has always shown warmup/generating status
            // in a toast; the AI-tool path dropped it on the floor, so a
            // two-minute generate_3d looked identical to a hang.
            const result = await opts.run(target, traceScene, ctx.onToolProgress);
            applyTransform(result.transform, position, rotation, scale);
            if (name) traceScene.renameSceneObject(result, name);
            ctx.onSceneChanged?.();
            return `Created "${result.name}" from ${typeLabel} "${objectRef}".`;
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            return `Error: ${msg}`;
        }
    };
}

function buildToolDefinition(opts: InferencePluginOptions): ToolDefinition {
    const properties = {
        ...COMMON_INPUT_PROPERTIES,
        ...opts.extraInputProperties,
    };
    const required = opts.objectOptional ? [] : ['object'];

    return {
        tool: {
            name: opts.toolName,
            input_schema: {
                type: 'object' as const,
                properties,
                ...(required.length > 0 ? { required } : {}),
            },
        },
        execute: opts.executeToolOverride ?? buildDefaultToolExecute(opts),
        ...(opts.toolModelLabel ? { modelLabel: opts.toolModelLabel } : {}),
    };
}

// ── Context Menu Builder ────────────────────────────────────

function buildContextMenu(opts: InferencePluginOptions): PluginContextMenu {
    const predicate = SOURCE_TYPE_CHECKS[opts.sourceType];
    return {
        label: opts.menuLabel,
        order: opts.menuOrder,
        predicate,
        run: opts.run,
    };
}

// ── Public API ──────────────────────────────────────────────

/**
 * Declare and register an inference plugin.
 * Converts the inference-specific shape into a PluginDeclaration
 * and registers it with the plugin registry.
 */
export function declareInferencePlugin(opts: InferencePluginOptions): void {
    const tool = buildToolDefinition(opts);
    const contextMenu = buildContextMenu(opts);

    const decl: PluginDeclaration = {
        id: opts.id,
        tool,
        slashCommands: opts.slashCommand ? [opts.slashCommand] : undefined,
        contextMenus: [contextMenu],
    };

    registerPlugin(decl);
}
